import { LoadingOutlined } from '@ant-design/icons';
import { QueryClient } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { Divider, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import BackSlideSvg from '../../../../assets/icons/BackSlideSvg';
import BackSlideSvgWhite from '../../../../assets/icons/BackSlideSvgWhite';
import CapSvg from '../../../../assets/icons/CapSvg';
import ClockSvg from '../../../../assets/icons/ClockSvg';
import CopyEditSvg from '../../../../assets/icons/CopyEditSvg';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import FullScreenSvgV2 from '../../../../assets/icons/FullScreenSvgV2';
import GroupSvg from '../../../../assets/icons/GroupSvg';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import NextSlideSvg from '../../../../assets/icons/NextSlideSvg';
import NextSlideSvgWhite from '../../../../assets/icons/NextSlideSvgWhite';
import PlaySvg from '../../../../assets/icons/PlaySvg';
import ProfileChecked from '../../../../assets/icons/ProfileChecked';
import { RightAngleSvg } from '../../../../assets/icons/RightAngleSvg';
import ShareFilledSvg from '../../../../assets/icons/ShareFilledSvg';
import ShareSvg from '../../../../assets/icons/ShareSvg';
import coverImage from '../../../../assets/images/app/emptyTemplateCoverPhoto.png';
import theme from '../../../../assets/theme';
import { Button } from '../../../../components/_atoms/Button';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import PlanTypeLabel from '../../../../components/_atoms/PlanTypeLabel';
import BaseModal from '../../../../components/_molecules/BaseModal';
import { GeneratorCard } from '../../../../components/_molecules/GeneratorCard';
import { HorisontalGeneratorCard } from '../../../../components/_molecules/HorisontalGeneratorCard';
import { HorisontalGeneratorCardV2 } from '../../../../components/_molecules/HorisontalGeneratorCardV2';
import { NewLandingPageFooter } from '../../../../components/_molecules/LandingPageFooter';
import ProgressBar from '../../../../components/_molecules/ProgressBar';
import ProgressBarV2 from '../../../../components/_molecules/ProgressBar/ProgressBarV2';
import ShareGlobalLesson from '../../../../components/_molecules/ShareGlobalLesson';
import {
  ConsumerGenerator,
  SampleLesson,
} from '../../../../components/_organisms/DesignerTabPane/GeneratorsTab/utils';
import GeneratorForm, {
  GeneratorFormState,
  GeneratorFormV2,
  useGeneratorFormState,
} from '../../../../components/_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import { FullScreenSlidePreview } from '../../../../components/FullScreenSlidePreview';
import NewAppLayout from '../../../../components/layouts/NewAppLayout';
import { ProfileCircle } from '../../../../components/ProfileCircle';
import { Slide } from '../../../../components/SlideComponents/slide.types';
import SlideWrapperViwer from '../../../../components/SlideComponents/SlideWrapperViewer';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useConsumeGeneratorAsNewPresentation from '../../../../hooks/useConsumeGeneratorAsNewPresentation';
import useCreateLessonFromSample from '../../../../hooks/useCreateLessonFromSample';
import useCreateLessonFromSampleEdit from '../../../../hooks/useCreateLessonFromSampleEdit';
import { useGeneratorCoverPhoto } from '../../../../hooks/useGeneratorCoverPhoto';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRoutePrefetch from '../../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { useSimilarGenerators } from '../../../../hooks/useSimilarGenerators';
import useSSRGeneratorNew from '../../../../hooks/useSSRGeneratorNew';
import { useTemplateCreatorPhoto } from '../../../../hooks/useTemplateCreatorPhoto';
import { RouterHistoryContext } from '../../../../providers/RouterHistoryProvider/RouterHistoryProvider';
import backendService from '../../../../services/backendService';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import {
  PromptInput,
  PromptInputConstraints,
} from '../../../../services/backendService/types';
import CoreAPIService from '../../../../services/CoreAPIService';
import {
  getDiscoverCreatorUrl,
  getGeneratorsUrl,
} from '../../../../services/DiscoverService/discoverUtils';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import { subjectOptions } from '../../../../utils/authenticatedHomeUtils';
import { getBaseUrl } from '../../../../utils/client';
import { typeToActivityMap } from '../../../../utils/creator';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';
import { provisionFlags } from '../../../../utils/ServerSideFeatureFlagUtils';

const NewProfileSection = ({ generator }: { generator: ConsumerGenerator }) => {
  const { thumbnail: creatorThumbnail } = useTemplateCreatorPhoto({
    creatorThumbnailId: generator.creatorThumbnailId,
    isPrivate: false,
  });

  return (
    <ProfileSectionContainer>
      <Link
        passHref
        href={getDiscoverCreatorUrl(generator.creatorName, generator.creatorId)}
      >
        <a>
          <ProfileCircle $radius={40} $showHoverAnimation={true}>
            {creatorThumbnail && (
              <img
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                src={creatorThumbnail}
                height={'10px'}
                width={'100%'}
                alt={`Profile picture of ${generator.creatorName}`}
              />
            )}
            {!creatorThumbnail && (
              <Image
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                src={coverImage}
                height={'100%'}
                width={'100%'}
                alt={`Profile picture of ${generator.creatorName}`}
              />
            )}
          </ProfileCircle>
        </a>
      </Link>
      <div>
        <Link
          passHref
          href={getDiscoverCreatorUrl(generator.creatorName, generator.creatorId)}
        >
          <a>
            <h2>
              {generator.creatorName}
              <span>
                <Tooltip title={'Verified'}>
                  <IconWrapper
                    style={{ marginLeft: 5 }}
                    height="16px"
                    width="16px"
                    color={'#ffa55b'}
                  >
                    <ProfileChecked />
                  </IconWrapper>
                </Tooltip>
              </span>
            </h2>
          </a>
        </Link>
        <p>Updated {dayjs(generator.modifiedAt).fromNow(true)} ago</p>
      </div>
    </ProfileSectionContainer>
  );
};

const NewGeneratorPreview = ({ authenticated }: { authenticated: boolean }) => {
  const { router } = useRouterMatch();

  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  const isDesktop = useBreakPoint(800);
  const [showShareModal, setShowShareModal] = useState(false);
  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const isRecommendationsEnabled = useFlag('GENERATOR_PAGE_RECOMMENDATIONS');
  const metrics = useMetrics();

  const { similarGenerators } = useSimilarGenerators({
    generatorId: router.query.generatorId as string,
  });

  return (
    <GeneratorPreviewContainer>
      {generator && (
        <>
          <GeneratorInfoContainer>
            <NewMainContainerWithGeneratorForm
              authenticated={authenticated}
              generator={generator}
            />
            <div style={{ maxWidth: 'max-content' }}>
              {generator.status !== 'published' && <Label>{generator.status}</Label>}
              {generator?.aiContent?.isPremiumGenerator && (
                <PlanTypeLabel
                  userAccessBadge={userAccessBadge}
                  generatorPlan={generator?.aiContent?.generatorPlan}
                  isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                />
              )}
              <h1 style={{ maxWidth: 'max-content' }}>{generator.title}</h1>
              <NewProfileSection generator={generator} />
              <BaseModal
                visible={showShareModal}
                onCancel={() => setShowShareModal(false)}
                title={'Share this customizable lesson'}
              >
                <ShareGlobalLesson
                  templateId={generator.id}
                  isGeneratedByAI={false}
                  isGenerator
                  title={generator.title || ''}
                />
              </BaseModal>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Tooltip mouseEnterDelay={1} title="Share this customizable lesson">
                  <IconWrapper
                    height="25px"
                    width="25px"
                    onClick={e => {
                      setShowShareModal(true);
                      e.stopPropagation();
                    }}
                    style={{ textDecoration: 'underline', gap: 5 }}
                  >
                    <ShareSvg /> Share
                  </IconWrapper>
                </Tooltip>
              </div>
              <div style={{ width: 'fit-content' }}>
                {isDesktop && <Divider />}
                <GeneratorStatsContainer>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="14px"
                        width="14px"
                        color="#828282"
                        style={{ gap: 5, color: '#828282', cursor: 'default' }}
                      >
                        <MagicWandSvg /> Customizations
                      </IconWrapper>
                      <span
                        aria-label="generations"
                        style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                      >
                        {generator?.downloadCount || '0'}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        color="#828282"
                        style={{ gap: 5, color: '#828282', cursor: 'default' }}
                      >
                        <PlaySvg /> Plays
                      </IconWrapper>
                      <span
                        aria-label="plays"
                        style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                      >
                        {generator?.playCount || '0'}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="20px"
                        width="20px"
                        color="#828282"
                        style={{
                          gap: 5,
                          display: 'flex',
                          cursor: 'default',
                          color: '#828282',
                        }}
                      >
                        <GroupSvg /> Students
                      </IconWrapper>
                      <span
                        aria-label="Students"
                        style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                      >
                        {generator?.participantCount || '0'}
                      </span>
                    </div>
                  </div>
                </GeneratorStatsContainer>

                {isDesktop && <Divider />}
              </div>
              <h2>Customizable lesson description</h2>
              <p
                style={{
                  width: '100%',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  maxWidth: 400,
                }}
              >
                {generator.description}
              </p>
            </div>
          </GeneratorInfoContainer>
          {isRecommendationsEnabled && generator && !!similarGenerators.length && (
            <RecommendationsContainer>
              <h2>You might want to try</h2>
              <HorisontalScrollContainer>
                {similarGenerators.map(aiGenerator => {
                  return (
                    <Link
                      href={getGeneratorsUrl({
                        title: aiGenerator.title,
                        generatorId: aiGenerator.id,
                      })}
                      key={aiGenerator.id}
                    >
                      <a style={{ height: '100%', width: 'fit-content' }}>
                        <HorisontalGeneratorCard
                          coverImageId={aiGenerator.coverImageId}
                          title={aiGenerator.title || ''}
                          status={aiGenerator.status}
                          firstPublished={aiGenerator.firstPublished}
                          isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                          generatorPlan={aiGenerator.aiContent?.generatorPlan}
                          userAccessBadge={userAccessBadge}
                          description={aiGenerator.description || undefined}
                          onClick={() => {
                            metrics.logEvent('Generator.ClickOnRecommendation', {
                              generatorId: generator.id,
                              clickedGeneratorId: aiGenerator.id,
                              from: 'generatorPage',
                            });
                          }}
                          learningStandards={aiGenerator.learningStandards}
                        />
                      </a>
                    </Link>
                  );
                })}
              </HorisontalScrollContainer>
            </RecommendationsContainer>
          )}
        </>
      )}
    </GeneratorPreviewContainer>
  );
};

const HorisontalScrollContainer = styled.ul`
  width: 100%;
  height: 210px;
  overflow-x: auto;
  overflow-y: hidden;

  display: flex;
  gap: 10px;
  padding: 10px 0px 10px 0px; // padding bottom to contain scroll bar
`;

const HorisontalScrollContainerV2 = styled.ul`
  width: 100%;
  height: 300px;
  overflow-x: auto;
  overflow-y: hidden;

  display: flex;
  gap: 10px;
  padding: 10px 0px 10px 0px; // padding bottom to contain scroll bar
`;

const GeneratorPreviewContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 60px;
  position: relative;
`;

const GeneratorInfoContainer = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  grid-template-columns: minmax(280px, 500px) auto;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: start;
  }
  @media (max-width: 800px) {
    align-items: center;
  }
`;

const RecommendationsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const GeneratorStatsContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 14px;
  padding: 0px 10px;
`;

const Label = styled.div`
  background: ${theme.colors.orange};
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 14px;
  padding: 2px 10px;
  width: fit-content;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
`;

const PageContainer = styled.div<{ $isAuthenticated?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

export const NewMainContainerWithGeneratorForm = ({
  generator,
  authenticated,
  searchPhrase,
  grade,
  isFullLessonEmptyState,
}: {
  generator: ConsumerGenerator;
  authenticated: boolean;
  searchPhrase?: string;
  grade?: string;
  isFullLessonEmptyState?: boolean;
}) => {
  const { tenantId, router } = useRouterMatch();
  const metrics = useMetrics();
  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
    error,
  } = useConsumeGeneratorAsNewPresentation(tenantId);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generator.id,
  );

  const handleConsumeGenerator = useCallback(
    (formState: GeneratorFormState) => {
      if (!authenticated) {
        showAuthGuard();
        return;
      }

      //if isFullLessonEmptyState we log the event
      if (isFullLessonEmptyState) {
        metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing.fullLesson', {
          generatorId: generator.id, //we can use this id to see which full lesson was consumed
          standard: searchPhrase,
          grade: grade,
          from: 'generatorPage',
        });
      }

      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator?.aiContent?.promptInputs || [],
      );
      consumeGenerator({
        generatorId: generator.id || '',
        userInputFormData,
        generatorName: generator.title || '',
        generatorPlan: generator?.aiContent?.generatorPlan,
        onSuccessFunction: template => {
          // redirect to the designer on desktop

          localStorage.setItem(
            '@curipod_v1_recentGeneratorConsume',
            JSON.stringify({
              generatorId: generator.id,
              formState,
            }),
          );

          router.push(
            `/${tenantId}/lessons/${template.id}/edit?generatorId=${generator.id}`,
          );
        },
      });
    },
    [
      authenticated,
      isFullLessonEmptyState,
      generator?.aiContent?.promptInputs,
      generator?.aiContent?.generatorPlan,
      generator.id,
      generator.title,
      consumeGenerator,
      showAuthGuard,
      metrics,
      searchPhrase,
      grade,
      router,
      tenantId,
    ],
  );

  return (
    <>
      {authGuardComponent}
      <GeneratorFormContainer>
        {(isLoading || data) && (
          <LoaderContainer>
            <ProgressBar isSmallTemplates />
          </LoaderContainer>
        )}
        {!(isLoading || data) && (
          <GeneratorFormBackgroundOld>
            <div
              style={{
                display: 'grid',
                width: '100%',
                gridTemplateColumns: '1fr',
                padding: '10px 3px',
              }}
            >
              <GeneratorCard
                subTitle=""
                coverImageId={generator?.coverImageId}
                title={generator.title || ''}
                description={generator.description || ''}
                learningStandards={generator.learningStandards}
              />
            </div>
            <GeneratorsForm
              promptInputs={generator?.aiContent?.promptInputs || []}
              handleConsumeGenerator={handleConsumeGenerator}
              isError={isError}
              error={error}
              promptInputSuggestions={generator?.aiContent?.promptInputSuggestions || []}
              preFillSearch={searchPhrase}
              preFillGrade={grade}
              isLoading={isLoading}
              isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
              generatorPlan={generator?.aiContent?.generatorPlan}
              generatorId={generator?.id}
            />
          </GeneratorFormBackgroundOld>
        )}
      </GeneratorFormContainer>
    </>
  );
};

export const NewMainContainerWithGeneratorFormV2 = ({
  generator,
  authenticated,
  searchPhrase,
  grade,
  isFullLessonEmptyState,
}: {
  generator: ConsumerGenerator;
  authenticated: boolean;
  searchPhrase?: string;
  grade?: string;
  isFullLessonEmptyState?: boolean;
}) => {
  const { tenantId, router } = useRouterMatch();
  const metrics = useMetrics();
  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
    error,
  } = useConsumeGeneratorAsNewPresentation(tenantId);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generator.id,
  );

  const handleConsumeGenerator = useCallback(
    (formState: GeneratorFormState) => {
      if (!authenticated) {
        showAuthGuard();
        return;
      }

      //if isFullLessonEmptyState we log the event
      if (isFullLessonEmptyState) {
        metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing.fullLesson', {
          generatorId: generator.id,
          standard: searchPhrase,
          grade: grade,
          from: 'generatorPage',
        });
      }

      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator?.aiContent?.promptInputs || [],
      );
      consumeGenerator({
        generatorId: generator.id || '',
        userInputFormData,
        generatorName: generator.title || '',
        generatorPlan: generator?.aiContent?.generatorPlan,
        onSuccessFunction: template => {
          // redirect to the designer on desktop

          localStorage.setItem(
            '@curipod_v1_recentGeneratorConsume',
            JSON.stringify({
              generatorId: generator.id,
              formState,
            }),
          );

          router.push(
            `/${tenantId}/lessons/${template.id}/edit?generatorId=${generator.id}`,
          );
        },
      });
    },
    [
      authenticated,
      isFullLessonEmptyState,
      generator?.aiContent?.promptInputs,
      generator?.aiContent?.generatorPlan,
      generator.id,
      generator.title,
      consumeGenerator,
      showAuthGuard,
      metrics,
      searchPhrase,
      grade,
      router,
      tenantId,
    ],
  );

  return (
    <>
      {authGuardComponent}
      <GeneratorFormContainerV2>
        {(isLoading || data) && (
          <LoaderContainerV2>
            <ProgressBarV2 isSmallTemplates />
          </LoaderContainerV2>
        )}
        {!(isLoading || data) && (
          <GeneratorFormBackground>
            <GeneratorsFormV2
              promptInputs={generator?.aiContent?.promptInputs || []}
              handleConsumeGenerator={handleConsumeGenerator}
              isError={isError}
              error={error}
              promptInputSuggestions={generator?.aiContent?.promptInputSuggestions || []}
              preFillSearch={searchPhrase}
              preFillGrade={grade}
              isLoading={isLoading}
              isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
              generatorPlan={generator?.aiContent?.generatorPlan}
              generatorId={generator?.id}
            />
          </GeneratorFormBackground>
        )}
      </GeneratorFormContainerV2>
    </>
  );
};

const GeneratorFormBackground = styled.div`
  padding: 20px;
  min-width: 340px;
  width: 90%;
  max-width: 580px;
`;

const GeneratorFormBackgroundOld = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11),
    0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13);
  max-width: 340px;
  position: sticky;
  top: 70px;

  @media (min-width: 500px) {
    min-width: 340px;
  }
`;

const LoaderContainer = styled(GeneratorFormBackground)`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  padding: 20px 5px;
  overflow: hidden;
`;

const LoaderContainerV2 = styled(GeneratorFormBackground)`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  padding: 20px 5px;
  overflow: hidden;
  background: transparent;
  padding: 20px 40px;
  border-radius: 10px;
`;

const GeneratorFormContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    justify-content: start;
  }
`;

const GeneratorFormContainerV2 = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  z-index: 1;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
  padding: 20px;
  min-width: 340px;
  width: 90%;
  max-width: 580px;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 5px;
  }
`;
const GeneratorsForm = ({
  promptInputs,
  isLoading,
  isError,
  error,
  handleConsumeGenerator,
  promptInputSuggestions,
  formState: existingFormState,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  preFillSearch,
  preFillGrade,
}: {
  promptInputs: PromptInput[];
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  handleConsumeGenerator: (formState: GeneratorFormState) => void;
  formState?: GeneratorFormState;
  promptInputSuggestions?: PromptInput[][];
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  preFillSearch?: string;
  preFillGrade?: string;
}) => {
  const metrics = useMetrics();
  const router = useRouter();
  const {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  } = useGeneratorFormState(promptInputs, existingFormState);

  const handleSubmit = useCallback(() => {
    //If the generator is consumed from a collection, log the event
    if (router.query.collectionId) {
      metrics.logEvent('Generator.ConsumeGeneratorFromCollection', {
        collectionId: router.query.collectionId as string,
        generatorId: generatorId,
        from: 'generatorPage',
      });
    }
    //If the generator is consumed from browsing standards, log the event
    if (router.query.standardId) {
      metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing', {
        standardId: router.query.standardId as string,
        subjectSelected: router.query.subject as string,
        gradeSelected: router.query.grade as string,
        generatorId: generatorId,
        from: 'generatorPage',
      });
    }

    if (router.query.fromLessonCategory) {
      metrics.logEvent('Generator.ConsumeGeneratorFromCreateWithAI', {
        generatorId: generatorId,
        selectedLessonCategory: router.query.fromLessonCategory as string,
        selectedCategory: router.query.fromCategory as string,
        from: 'createPage',
      });
    }

    handleConsumeGenerator(formState);
  }, [
    formState,
    generatorId,
    handleConsumeGenerator,
    metrics,
    router.query.collectionId,
    router.query.grade,
    router.query.standardId,
    router.query.subject,
    router.query.fromLessonCategory,
    router.query.fromCategory,
  ]);

  return (
    <GeneratorForm
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      promptInputSuggestions={promptInputSuggestions}
      promptInputs={promptInputs}
      formState={formState}
      isLoading={isLoading}
      isError={isError}
      error={error}
      isPremiumGenerator={isPremiumGenerator}
      generatorPlan={generatorPlan}
      generatorId={generatorId}
      upgradeLocation="GeneratorPage"
      preFillSearch={preFillSearch}
      preFillGrade={preFillGrade}
      handleMultipleInputsChange={handleMultipleInputsChange}
    />
  );
};

const GeneratorsFormV2 = ({
  promptInputs,
  isLoading,
  isError,
  error,
  handleConsumeGenerator,
  promptInputSuggestions,
  formState: existingFormState,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  preFillSearch,
  preFillGrade,
}: {
  promptInputs: PromptInput[];
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  handleConsumeGenerator: (formState: GeneratorFormState) => void;
  formState?: GeneratorFormState;
  promptInputSuggestions?: PromptInput[][];
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  preFillSearch?: string;
  preFillGrade?: string;
}) => {
  const metrics = useMetrics();
  const router = useRouter();
  const {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  } = useGeneratorFormState(promptInputs, existingFormState);

  const handleSubmit = useCallback(() => {
    //If the generator is consumed from a collection, log the event
    if (router.query.collectionId) {
      metrics.logEvent('Generator.ConsumeGeneratorFromCollection', {
        collectionId: router.query.collectionId as string,
        generatorId: generatorId,
        from: 'generatorPage',
      });
    }
    //If the generator is consumed from browsing standards, log the event
    if (router.query.standardId) {
      metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing', {
        standardId: router.query.standardId as string,
        subjectSelected: router.query.subject as string,
        gradeSelected: router.query.grade as string,
        generatorId: generatorId,
        from: 'generatorPage',
      });
    }

    if (router.query.fromLessonCategory) {
      metrics.logEvent('Generator.ConsumeGeneratorFromCreateWithAI', {
        generatorId: generatorId,
        selectedLessonCategory: router.query.fromLessonCategory as string,
        selectedCategory: router.query.fromCategory as string,
        from: 'createPage',
      });
    }

    handleConsumeGenerator(formState);
  }, [
    formState,
    generatorId,
    handleConsumeGenerator,
    metrics,
    router.query.collectionId,
    router.query.grade,
    router.query.standardId,
    router.query.subject,
    router.query.fromLessonCategory,
    router.query.fromCategory,
  ]);

  return (
    <GeneratorFormV2
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      promptInputSuggestions={promptInputSuggestions}
      promptInputs={promptInputs}
      formState={formState}
      isLoading={isLoading}
      isError={isError}
      error={error}
      isPremiumGenerator={isPremiumGenerator}
      generatorPlan={generatorPlan}
      generatorId={generatorId}
      upgradeLocation="GeneratorPage"
      preFillSearch={preFillSearch}
      preFillGrade={preFillGrade}
      handleMultipleInputsChange={handleMultipleInputsChange}
    />
  );
};

const ProfileSectionContainer = styled.div`
  display: flex;
  padding-left: 5px;
  padding-right: 5px;
  box-shadow: none;
  border: none;
  justify-content: flex-start;
  width: 100%;
  align-items: center;
  margin: 20px 0px;
  a {
    &:hover {
      text-decoration: underline;
    }
  }

  h2 {
    display: flex;
    align-items: center;
  }

  span {
    display: flex;
    align-items: baseline;
    padding-right: 6px;
  }

  h2,
  p {
    margin: 0px 0px 0px 20px;
    font-size: 14px;
  }

  p {
    color: ${theme.colors.text.dark};
  }

  @media (max-width: 900px) {
    p {
      font-size: 11px;
    }
  }
`;

export const CoverPhotoRenderer = ({
  id,
  coverImageId,
  coverImageAltText,
}: {
  id: string;
  coverImageId: string;
  coverImageAltText?: string | null;
}) => {
  const { thumbnail, isLoading } = useGeneratorCoverPhoto(id, coverImageId);

  if (!thumbnail && isLoading) {
    return <div style={{ width: '100%', height: '100%', background: 'white' }}></div>;
  }

  return (
    <PhotoImage
      src={thumbnail}
      decoding="async"
      alt={coverImageAltText ? coverImageAltText : undefined}
    />
  );
};

const PhotoImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const NewGeneratorHead = () => {
  const { router } = useRouterMatch();
  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  if (!generator) return null;
  const description = `${generator.description} | A customizable lesson created by Curipod pedagogy experts`;
  return (
    <Head>
      <title>
        {generator.title} by {generator.creatorName} - Curipod
      </title>
      <meta
        property="og:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
      />
      <meta name="description" content={description} key="namedescription" />
      <meta property="description" content={description} key="description" />
      <meta property="og:description" content={description} key="og:description" />
      <meta name="twitter:description" content={description} />
      <meta
        property="og:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imageurl"
      />
      <meta
        property="og:image:secure_url"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/svg+xml'} key="imagetype" />
      <meta
        property="og:image:alt"
        content={generator.description || ''}
        key="imageAlt"
      />
      <meta property="og:image:width" content={'160'} key="imageWidth" />
      <meta property="og:image:height" content={'108'} key="imageHeight" />
      <meta
        name="twitter:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
      />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const GeneratorPage = () => {
  const session = useSession();
  const { router } = useRouterMatch();

  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  // Designer page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });

  // Mobile preview page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`, undefined, {
    priority: true,
  });

  return (
    <>
      {generator?.aiContent?.durationInMins ? (
        <PageContainer>
          <NewGeneratorHead />
          <NewGeneratorPreviewV2 authenticated={session.status === 'authenticated'} />
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      ) : (
        <NewAppLayout allowUnAuthenticated={true}>
          <PageContainer $isAuthenticated={session.status === 'authenticated'}>
            <NewGeneratorHead />
            <NewGeneratorPreview authenticated={session.status === 'authenticated'} />
            <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
          </PageContainer>
        </NewAppLayout>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const queryClient = new QueryClient();
  const session = await getSession({ req: context.req });

  const { generatorId } = context.query as { generatorId: string };
  const flags = await provisionFlags(context, queryClient, session);

  await queryClient.prefetchQuery(
    ['generator-retrieve', generatorId],
    async () => {
      return CoreAPIService.fetchSingleGeneratorElastic(generatorId);
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  return {
    props: {
      ...flags,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      session,
    },
  };
};

export default GeneratorPage;

const findLessonContent = (sampleLessonId: string, samples?: SampleLesson[]) => {
  const lesson = samples?.find(sample => sample.id === sampleLessonId);
  if (!lesson) return { slides: [], lessonAttachments: [] };
  return { slides: lesson.slides || [], lessonAttachments: lesson.lessonAttachments };
};

const NewGeneratorPreviewV2 = ({ authenticated }: { authenticated: boolean }) => {
  const { tenantId, router } = useRouterMatch();
  const ismobile = useIsMobile();
  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSlidesModalVisible, setIsSlidesModalVisible] = useState(false);
  const [modalCurrentLessonIndex, setModalCurrentLessonIndex] = useState<
    undefined | number
  >(undefined);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generator?.id,
  );

  const sampleLessonsWithFilteredSlides = useMemo(() => {
    return generator?.sampleLessons?.map(lesson => {
      const slides =
        lesson?.slides?.filter(
          r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
        ) || [];
      const constraints = generator?.aiContent?.promptInputs?.map(input => {
        return {
          id: input.id,
          constraints: input.constraints,
        };
      });
      return {
        ...lesson,
        slides,
        constraints,
      };
    });
  }, [generator]);

  const { previousRoute } = useContext(RouterHistoryContext);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const { userAccessBadge, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const isRecommendationsEnabled = useFlag('GENERATOR_PAGE_RECOMMENDATIONS');
  const metrics = useMetrics();
  const generatorBackgroundImageUrl = useMemo(() => {
    if (!generator || !generator.aiContent?.backgroundImageId) return null;
    const backgroundUrl = backendService.instance.generatorPlugin.getGeneratorThumbnailUrl(
      generator.aiContent?.backgroundImageId,
    );
    return backgroundUrl;
  }, [generator]);

  const { similarGenerators } = useSimilarGenerators({
    generatorId: router.query.generatorId as string,
  });

  const { createLesson, isCreatingLesson } = useCreateLessonFromSample(tenantId);
  const { createLessonAndEdit, isCreatingLessonAndEdit } = useCreateLessonFromSampleEdit(
    tenantId,
  );

  const slideActivites = useMemo(() => {
    return uniq(
      generator?.slides
        ?.filter(
          s => s.type !== 'podium' && s.type !== 'responsegrid' && s.type !== 'slide',
        )
        ?.map(slide => {
          return typeToActivityMap(slide.type)?.title;
        }) || [],
    ).filter(Boolean);
  }, [generator?.slides]);

  const handleExpandDescription = () => {
    if (isDescriptionExpanded) {
      setIsDescriptionExpanded(false);
      return;
    }
    setIsDescriptionExpanded(true);
  };

  const handleBackClick = () => {
    if (!previousRoute) {
      router.push(`/${tenantId}/lessons`);
      return;
    }
    router.back();
  };

  const handleNextSlide = () => {
    if (
      sampleLessonsWithFilteredSlides?.[0].slides?.length &&
      currentSlideIndex < sampleLessonsWithFilteredSlides?.[0].slides?.length - 1
    ) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePreviousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const handlePresentButtonClick = async (
    presentationId: string,
    slides: Slide[],
    lessonAttachments?: LessonAttachment[],
  ) => {
    if (isCreatingLesson) return;

    if (!authenticated) {
      showAuthGuard();
      return;
    }

    if (
      generator?.aiContent?.generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({
        label: 'Present lesson',
        description: `Generator: ${generator?.title}`,
      });
      return;
    }

    if (
      generator?.aiContent?.generatorPlan === GeneratorCoarseLicenseLevel.premium &&
      !(
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise ||
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.premium
      )
    ) {
      promptPaywall({
        label: 'Present lesson',
        description: `Generator: ${generator?.title}`,
      });
      return;
    }

    createLesson({
      presentationId,
      slides,
      tenantId,
      lessonAttachments,
    });
  };

  const handleCopyAndEditButtonClick = async (
    presentationId: string,
    slides: Slide[],
    lessonAttachments?: LessonAttachment[],
  ) => {
    if (isCreatingLessonAndEdit) return;

    if (!authenticated) {
      showAuthGuard();
      return;
    }

    if (
      generator?.aiContent?.generatorPlan === GeneratorCoarseLicenseLevel.enterprise &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({
        label: 'Copy and edit lesson',
        description: `Generator: ${generator?.title}`,
      });
      return;
    }

    if (
      generator?.aiContent?.generatorPlan === GeneratorCoarseLicenseLevel.premium &&
      !(
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise ||
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.premium
      )
    ) {
      promptPaywall({
        label: 'Copy and edit lesson',
        description: `Generator: ${generator?.title}`,
      });
      return;
    }

    createLessonAndEdit({
      presentationId,
      slides,
      tenantId,
      lessonAttachments,
    });
  };

  const sampleLessonInputValue = useCallback(
    ({
      input,
      constraints,
    }: {
      input: PromptInput;
      constraints:
        | { id: string; constraints: PromptInputConstraints | undefined }[]
        | undefined;
    }) => {
      //TODO refactor this
      if (input.type === 'dropdown' && constraints) {
        const dropdownLabel = constraints.find(constraint => {
          return constraint.id === input.id;
        });

        if (dropdownLabel && dropdownLabel?.constraints?.options) {
          const selectedOption = dropdownLabel.constraints.options.find(
            option => option.value === input.value,
          );
          return selectedOption ? selectedOption.displayName : input.value;
        }

        return input.value; // Fallback if no display name is found
      }

      return input.value;
    },
    [],
  );

  return (
    <GeneratorPreviewContainer style={{ paddingTop: 0 }}>
      {generator && (
        <>
          {authGuardComponent}
          {paywallComponent}
          <TopSection $backgroundImageUrl={generatorBackgroundImageUrl || ''}>
            <TopSectionContent>
              <TopSectionRow1>
                <BackContainer
                  onClick={() => {
                    handleBackClick();
                  }}
                >
                  <RightAngleSvg />
                  <StyledText>Back</StyledText>
                </BackContainer>

                <ShareContainer
                  onClick={e => {
                    setShowShareModal(true);
                    e.stopPropagation();
                  }}
                >
                  <IconWrapper
                    height="25px"
                    width="25px"
                    onClick={e => {
                      setShowShareModal(true);
                      e.stopPropagation();
                    }}
                    style={{
                      textDecoration: 'underline',
                      gap: 5,
                      color: theme.colors.white,
                    }}
                  >
                    <ShareFilledSvg />
                  </IconWrapper>

                  <StyledText>Share</StyledText>
                </ShareContainer>
              </TopSectionRow1>
              <TopSectionRow2>
                <div
                  style={{
                    margin: 'auto 0px',
                    gap: 20,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Title $fontSize={48}>{generator.title}</Title>

                  <GetLessonButton
                    styles={
                      ismobile
                        ? {
                            width: 'fit-content',
                          }
                        : {}
                    }
                    onClick={() => {
                      const element = document.getElementById('get-lesson');
                      if (!element) return;
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'start',
                      });
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 10,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper height="25px" width="25px">
                        <MagicWandSvg />
                      </IconWrapper>

                      <span>Get Lesson</span>
                    </div>
                  </GetLessonButton>
                </div>
              </TopSectionRow2>
              <TopSectionRow3>
                {generator.aiContent?.generatorPlan ===
                  GeneratorCoarseLicenseLevel.enterprise ||
                generator.aiContent?.generatorPlan ===
                  GeneratorCoarseLicenseLevel.premium ? (
                  <Tooltip
                    title={
                      userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.premium ||
                      userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                        ? `Available to you as you're on the school plan`
                        : 'This lesson is accessible on the school plan'
                    }
                  >
                    <div
                      style={{ display: 'flex', cursor: 'pointer' }}
                      onClick={() => {
                        if (
                          userAccessBadge?.license.coarseLevel !==
                            LicenseCoarseLevels.premium &&
                          userAccessBadge?.license.coarseLevel !==
                            LicenseCoarseLevels.enterprise
                        ) {
                          promptPaywall({
                            label: 'Upgrade account',
                            description: `Generator: ${generator?.title}`,
                          });
                        }
                      }}
                    >
                      <IconWrapper
                        height="25px"
                        width="25px"
                        style={{ marginRight: 10 }}
                        color={
                          userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.premium ||
                          userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                            ? theme.colors.white
                            : theme.colors.yellow
                        }
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                      <StyledTextSaveToCollection>
                        Premium Lesson
                      </StyledTextSaveToCollection>
                    </div>
                  </Tooltip>
                ) : (
                  <div />
                )}
              </TopSectionRow3>
            </TopSectionContent>
          </TopSection>

          <DetailSection>
            <DetailSectionLeft>
              <Title $fontSize={32} style={{ color: theme.colors.black }}>
                Description
              </Title>

              <DescriptionText>
                {isDescriptionExpanded ||
                (generator?.description && generator?.description?.length < 500)
                  ? generator.description
                  : `${generator?.description?.slice(0, 500)}...`}
              </DescriptionText>
              {generator?.description && generator?.description?.length > 500 && (
                <Button
                  onClick={handleExpandDescription}
                  type="ghost"
                  styles={{ width: 'fit-content', border: 'none', alignSelf: 'flex-end' }}
                  icon={
                    <IconWrapper
                      height="25px"
                      width="25px"
                      style={{
                        transform: isDescriptionExpanded
                          ? 'rotate(90deg)'
                          : 'rotate(-90deg)',
                      }}
                    >
                      <ArrowLeftSvg />
                    </IconWrapper>
                  }
                >
                  {isDescriptionExpanded ? 'see less' : 'See more'}
                </Button>
              )}
            </DetailSectionLeft>
            <DetailSectionRight>
              {!!generator.subjects.length && (
                <DetailsGroup>
                  <DetailsRightTitle>SUBJECTS</DetailsRightTitle>
                  <DetailsChipContainer>
                    {generator.subjects.map(subject => (
                      <DetailsChip key={subject}>
                        <DetailsChipDarkText>
                          {
                            subjectOptions.find(option => option.value === subject)
                              ?.displayName
                          }
                        </DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator.grades.length && (
                <DetailsGroup>
                  <DetailsRightTitle>GRADES</DetailsRightTitle>
                  <DetailsChipContainer>
                    {groupedGrades(generator.grades)}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!slideActivites.length && (
                <DetailsGroup>
                  <DetailsRightTitle>INTERACTIVE ACTIVITIES</DetailsRightTitle>
                  <DetailsChipContainer>
                    {slideActivites.map((activity, index) => (
                      <DetailsChip key={index}>
                        <DetailsChipDarkText>{activity}</DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator.aiContent?.durationInMins && (
                <DetailsGroup>
                  <DetailsRightTitle>DURATION</DetailsRightTitle>
                  <DetailsChipContainer>
                    <DetailsChip>
                      <DetailsChipDarkText>
                        <ClockSvg /> {generator.aiContent?.durationInMins} minute
                        {generator.aiContent?.durationInMins === 1 ? '' : 's'}
                      </DetailsChipDarkText>
                    </DetailsChip>
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator?.learningStandards?.length && (
                <DetailsGroup>
                  <DetailsRightTitle>STANDARDS</DetailsRightTitle>
                  <DetailsChipContainer>
                    {generator?.learningStandards?.map(standard => (
                      <DetailsChip key={standard.id}>
                        <DetailsChipDarkText>{standard.label}</DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}
            </DetailSectionRight>
          </DetailSection>

          {!!sampleLessonsWithFilteredSlides?.length && (
            <DemoLessonContainer>
              {sampleLessonsWithFilteredSlides?.map((lesson, index) => {
                return (
                  <DemoLessonInnerContainer key={lesson.id}>
                    {ismobile && (
                      <LessonDetailsTitle>
                        Ready made lesson {index + 1}
                      </LessonDetailsTitle>
                    )}
                    <DemoLessonSlidesContainer>
                      <SlideWrapperViwer
                        aspect={{ height: 1080, width: 1920 }}
                        slide={lesson?.slides[currentSlideIndex]}
                        backgroundMedia={
                          lesson?.slides[currentSlideIndex].backgroundMedia
                        }
                        resultView={false}
                        showStaticPoll={true}
                        hideLogo={false}
                        isDiscoverTemplate={true}
                        lessonAttachments={lesson?.lessonAttachments}
                        isOwner={false}
                      ></SlideWrapperViwer>

                      <SlidesControlContainer>
                        <SlidesControlLeftContainer>
                          <StyledIconWrapper
                            height="20px"
                            width="20px"
                            style={{
                              opacity: currentSlideIndex === 0 ? 0.2 : 1,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                            onClick={() => {
                              metrics.logEvent(
                                'Generator.ClickOnPreviousSlideSampleLesson',
                                {
                                  generatorId: generator.id,
                                  lessonNumber: index + 1,
                                  currentSlideNumber: currentSlideIndex + 1,
                                  inModal: false,
                                  from: 'generatorPage',
                                },
                              );
                              handlePreviousSlide();
                            }}
                          >
                            <BackSlideSvg />
                          </StyledIconWrapper>

                          <SlideControllerText>
                            Slide {currentSlideIndex + 1}/{lesson.slides.length}
                          </SlideControllerText>

                          <StyledIconWrapper
                            height="20px"
                            width="20px"
                            style={{
                              opacity:
                                currentSlideIndex === lesson.slides.length - 1 ? 0.2 : 1,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                            onClick={() => {
                              metrics.logEvent('Generator.ClickOnNextSlideSampleLesson', {
                                generatorId: generator.id,
                                lessonNumber: index + 1,
                                currentSlideNumber: currentSlideIndex + 1,
                                inModal: false,
                                from: 'generatorPage',
                              });
                              handleNextSlide();
                            }}
                          >
                            <NextSlideSvg />
                          </StyledIconWrapper>
                        </SlidesControlLeftContainer>
                        <SlidesControlRightContainer
                          onClick={() => {
                            setIsSlidesModalVisible(true);
                            setModalCurrentLessonIndex(index);
                            metrics.logEvent('Generator.ClickOnFullScreenLesson', {
                              generatorId: generator.id,
                              lessonNumber: index + 1,
                              from: 'generatorPage',
                            });
                          }}
                        >
                          <StyledIconWrapper height="20px" width="20px">
                            <FullScreenSvgV2 />
                          </StyledIconWrapper>
                        </SlidesControlRightContainer>
                      </SlidesControlContainer>
                    </DemoLessonSlidesContainer>
                    <DemoLessonDetailsContainer>
                      {!ismobile && (
                        <LessonDetailsTitle>
                          Ready made lesson {index + 1}
                        </LessonDetailsTitle>
                      )}
                      <DemoLessonDetailsInfoContainer>
                        {lesson.inputs.map(input => {
                          const label =
                            generator?.aiContent?.promptInputs.find(
                              promptInput => promptInput.id === input.id,
                            )?.label || '';

                          return (
                            <LessonInfoContainer key={input.id}>
                              <LessonDetailsLabel>{label}</LessonDetailsLabel>
                              <LessonDetailsValue>
                                <TextWithToggle
                                  text={
                                    sampleLessonInputValue({
                                      input,
                                      constraints: lesson.constraints,
                                    }) || ''
                                  }
                                  limit={200}
                                />
                              </LessonDetailsValue>
                            </LessonInfoContainer>
                          );
                        })}
                      </DemoLessonDetailsInfoContainer>

                      <PresentButton
                        onClick={() => {
                          metrics.logEvent('Generator.ClickOnPresentSampleLesson', {
                            generatorId: generator.id,
                            lessonNumber: index + 1,
                            from: 'generatorPage',
                          });
                          const { lessonAttachments, slides } = findLessonContent(
                            lesson.id,
                            generator?.sampleLessons,
                          );
                          handlePresentButtonClick(
                            generator.id,
                            slides,
                            lessonAttachments,
                          );
                        }}
                      >
                        <IconWrapper height="16px" width="16px">
                          {isCreatingLesson ? (
                            <Spin
                              indicator={
                                <LoadingOutlined style={{ fontSize: 16 }} spin />
                              }
                            />
                          ) : (
                            <PlaySvg />
                          )}
                        </IconWrapper>
                        <PresentButtonText>Start now</PresentButtonText>
                      </PresentButton>

                      <CopyEditButton
                        onClick={() => {
                          metrics.logEvent('Generator.ClickOnCopyAndEditSampleLesson', {
                            generatorId: generator.id,
                            lessonNumber: index + 1,
                            from: 'generatorPage',
                          });

                          const { lessonAttachments, slides } = findLessonContent(
                            lesson.id,
                            generator?.sampleLessons,
                          );

                          handleCopyAndEditButtonClick(
                            generator.id,
                            slides,
                            lessonAttachments,
                          );
                        }}
                      >
                        <IconWrapper height="16px" width="16px">
                          {isCreatingLessonAndEdit ? (
                            <Spin
                              indicator={
                                <LoadingOutlined style={{ fontSize: 16 }} spin />
                              }
                            />
                          ) : (
                            <CopyEditSvg />
                          )}
                        </IconWrapper>
                        <CopyEditButtonText>Copy & Edit</CopyEditButtonText>
                      </CopyEditButton>
                    </DemoLessonDetailsContainer>
                  </DemoLessonInnerContainer>
                );
              })}

              {modalCurrentLessonIndex !== undefined &&
                sampleLessonsWithFilteredSlides?.length &&
                isSlidesModalVisible && (
                  <FullScreenSlidePreview onClose={() => setIsSlidesModalVisible(false)}>
                    <DemoLessonSlidesContainerV2>
                      <SlideWrapperViwer
                        aspect={{ height: 1080, width: 1920 }}
                        slide={
                          sampleLessonsWithFilteredSlides[modalCurrentLessonIndex]
                            ?.slides[currentSlideIndex]
                        }
                        backgroundMedia={
                          sampleLessonsWithFilteredSlides[modalCurrentLessonIndex]
                            ?.slides[currentSlideIndex].backgroundMedia
                        }
                        resultView={false}
                        showStaticPoll={true}
                        hideLogo={false}
                        isDiscoverTemplate={true}
                        lessonAttachments={
                          sampleLessonsWithFilteredSlides[modalCurrentLessonIndex]
                            ?.lessonAttachments
                        }
                        isOwner={false}
                      ></SlideWrapperViwer>

                      <SlidesControlContainerV2>
                        <SlidesControlLeftContainer>
                          <StyledIconWrapperV2
                            height="24px"
                            width="24px"
                            color="white"
                            style={{
                              opacity: currentSlideIndex === 0 ? 0.2 : 1,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                            onClick={() => {
                              metrics.logEvent(
                                'Generator.ClickOnPreviousSlideSampleLesson',
                                {
                                  generatorId: generator.id,
                                  lessonNumber: modalCurrentLessonIndex + 1,
                                  currentSlideNumber: currentSlideIndex + 1,
                                  inModal: true,
                                  from: 'generatorPage',
                                },
                              );
                              handlePreviousSlide();
                            }}
                          >
                            <BackSlideSvgWhite />
                          </StyledIconWrapperV2>

                          <SlideControllerTextV2>
                            Slide {currentSlideIndex + 1}/
                            {
                              sampleLessonsWithFilteredSlides[modalCurrentLessonIndex]
                                ?.slides.length
                            }
                          </SlideControllerTextV2>

                          <StyledIconWrapperV2
                            height="24px"
                            width="24px"
                            color="white"
                            style={{
                              opacity:
                                currentSlideIndex ===
                                sampleLessonsWithFilteredSlides[modalCurrentLessonIndex]
                                  ?.slides.length -
                                  1
                                  ? 0.2
                                  : 1,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                            onClick={() => {
                              metrics.logEvent('Generator.ClickOnNextSlideSampleLesson', {
                                generatorId: generator.id,
                                lessonNumber: modalCurrentLessonIndex + 1,
                                currentSlideNumber: currentSlideIndex + 1,
                                inModal: true,
                                from: 'generatorPage',
                              });
                              handleNextSlide();
                            }}
                          >
                            <NextSlideSvgWhite />
                          </StyledIconWrapperV2>
                        </SlidesControlLeftContainer>
                      </SlidesControlContainerV2>
                    </DemoLessonSlidesContainerV2>
                  </FullScreenSlidePreview>
                )}
            </DemoLessonContainer>
          )}

          <CustomizeSection $backgroundImageUrl={generatorBackgroundImageUrl || ''}>
            <CustomizeSectionContent>
              <CustomizeTitle id="get-lesson">
                Customize into your own lesson
              </CustomizeTitle>
              <NewMainContainerWithGeneratorFormV2
                authenticated={authenticated}
                generator={generator}
              />
            </CustomizeSectionContent>
          </CustomizeSection>

          <BaseModal
            visible={showShareModal}
            onCancel={() => setShowShareModal(false)}
            title={'Share this customizable lesson'}
          >
            <ShareGlobalLesson
              templateId={generator.id}
              isGeneratedByAI={false}
              isGenerator
              title={generator.title || ''}
            />
          </BaseModal>
          {/* <BaseModalV2
            visible={isCustomizeModalVisible}
            onCancel={() => setIsCustomizeModalVisible(false)}
            width={ismobile ? '95%' : '70%'}
            transparentBackground
            style={{
              borderRadius: '50px',
              zIndex: shouldHideModal ? '-1 !important' : '3000',
            }}
            maskStyle={{
              zIndex: shouldHideModal ? '-1 !important' : '3000',
            }}
            zIndex={1}
          >
            <CustomizeModalContent
              $backgroundImageUrl={generatorBackgroundImageUrl || ''}
              style={{}}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <div
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  gap: '20px',
                  padding: '20px 10px',
                }}
              >
                <CustomizeModalTitle>Customize into your own lesson</CustomizeModalTitle>
                <div>
                  <NewMainContainerWithGeneratorFormV2
                    authenticated={authenticated}
                    generator={generator}
                    setShouldHideModal={setShouldHideModal}
                  />
                </div>
              </div>
            </CustomizeModalContent>
          </BaseModalV2> */}

          {isRecommendationsEnabled && generator && !!similarGenerators.length && (
            <RecommendationsContainer>
              <h1>You might want to try</h1>
              <HorisontalScrollContainerV2>
                {similarGenerators.map(aiGenerator => {
                  return (
                    <Link
                      href={getGeneratorsUrl({
                        title: aiGenerator.title,
                        generatorId: aiGenerator.id,
                      })}
                      key={aiGenerator.id}
                    >
                      <a style={{ height: '100%', width: 'fit-content' }}>
                        <HorisontalGeneratorCardV2
                          coverImageId={aiGenerator.coverImageId}
                          title={aiGenerator.title || ''}
                          status={aiGenerator.status}
                          firstPublished={aiGenerator.firstPublished}
                          isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                          generatorPlan={aiGenerator.aiContent?.generatorPlan}
                          userAccessBadge={userAccessBadge}
                          description={aiGenerator.description || undefined}
                          onClick={() => {
                            metrics.logEvent('Generator.ClickOnRecommendation', {
                              generatorId: generator.id,
                              clickedGeneratorId: aiGenerator.id,
                              from: 'generatorPage',
                            });
                          }}
                          learningStandards={aiGenerator.learningStandards}
                        />
                      </a>
                    </Link>
                  );
                })}
              </HorisontalScrollContainerV2>
            </RecommendationsContainer>
          )}
        </>
      )}
    </GeneratorPreviewContainer>
  );
};

const groupedGrades = (grades: string[]) => {
  const formattedGrade = (grade: number) => {
    switch (grade) {
      case 0: // Kindergarten
        return 'Kindergarten';
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return grade + 'th';
    }
  };

  const sortedGrades = grades.sort((a, b) => parseInt(a) - parseInt(b));
  const groupedGrades = [];
  let start = 0;
  let end = 0;

  for (let i = 0; i <= sortedGrades.length; i++) {
    if (i === 0) {
      start = parseInt(sortedGrades[i]);
      end = start;
      continue;
    }
    if (parseInt(sortedGrades[i]) - end === 1) {
      end = parseInt(sortedGrades[i]);
    } else {
      if (start === end) {
        groupedGrades.push(formattedGrade(start));
      } else {
        groupedGrades.push(`${formattedGrade(start)}-${formattedGrade(end)}`);
      }
      start = parseInt(sortedGrades[i]);
      end = start;
    }
  }

  return (
    <>
      {groupedGrades.map(grade => (
        <DetailsChip key={grade}>
          <DetailsChipDarkText>
            <CapSvg /> {grade}
          </DetailsChipDarkText>
        </DetailsChip>
      ))}
    </>
  );
};

const TopSection = styled.div<{ $backgroundImageUrl: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
  min-height: calc(80vh - 60px);
  max-height: fit-content;
  height: fit-content;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: row;
  padding: 40px 80px;
  gap: 50px;
  width: 100%;
  max-width: 1400px;
  margin: auto;

  @media (max-width: 767px) {
    padding: 40px 20px;
    flex-direction: column-reverse;
  }
`;

const DetailSectionLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 50%;

  @media (max-width: 1000px) {
    width: 100%;
  }

  button {
    display: flex;
    flex-direction: row-reverse;
    box-shadow: none;

    svg {
      margin: 0;
    }

    &:hover,
    &:focus,
    &:active {
      box-shadow: none;
      background: none;
    }
  }
`;

const DetailSectionRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 8px solid black;
  border-radius: 5px;
  padding: 30px;
  gap: 30px;
  width: 50%;

  @media (max-width: 1000px) {
    width: 100%;
  }
`;

const CustomizeSection = styled.div<{ $backgroundImageUrl: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: calc(70vh - 60px);
  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
`;

const TopSectionContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  padding: 40px 80px;
  flex-direction: column;
  background-color: rgba(0, 0, 0, 0.6); // Black overlay with 0.6 opacity
  height: 100%;
  gap: 20px;

  @media (max-width: 767px) {
    padding: 40px 20px;
  }
`;

const CustomizeSectionContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  padding: 40px 80px;
  flex-direction: column;
  gap: 20px;
  background-color: rgba(0, 0, 0, 0.6); // Black overlay with 0.6 opacity
  height: 100%;
  justify-content: center;
  align-items: center;

  @media (max-width: 767px) {
    padding: 40px 20px;
  }

    button {
  background: ${theme.colors.white};
  color: ${theme.colors.black};

  &:hover, &:focus, &:active {
    background: ${theme.colors.grey};
    color: ${theme.colors.black};
  }
`;

const TopSectionRow1 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const TopSectionRow2 = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: auto 0px;
  height: fit-content;
  flex-grow: 1;
  width: 100%;
  max-width: 500px;

  @media (max-width: 1000px) {
    max-width: 80%;
  }
  @media (max-width: 860px) or (max-height: 860px) {
    max-width: 50%;
  }

  button {
  background: ${theme.colors.white};
  color: ${theme.colors.black};

  &:hover, &:focus, &:active {
    background: ${theme.colors.grey};
    color: ${theme.colors.black};
  }
`;

const TopSectionRow3 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledText = styled.p`
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  dixplay: flex;
  align-items: center;
  margin: 0px;
`;

const StyledTextSaveToCollection = styled.p`
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  dixplay: flex;
  align-items: center;
  margin: 0px;
  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const BackContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ShareContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: 800;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  margin: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 10}px;
  }
`;

const DescriptionText = styled.p`
  color: #000;

  font-family: Nunito;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 136.952%;
  white-space: pre-wrap;

  @media (max-width: 800px) {
    font-size: 18px;
  }
`;

const DetailsRightTitle = styled.text`
  color: #565760;

  font-family: Nunito;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const DetailsGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsChip = styled.div`
  align-self: flex-start;
  margin-top: 10px;

  display: flex;
  padding: 2px 10px;
  justify-content: center;
  align-items: center;
  gap: 5px;

  border-radius: 20px;
  background: #fff;

  /* Barely floating */
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
`;

const DetailsChipDarkText = styled.text`
  color: #000;

  font-family: Nunito;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const DetailsChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
`;

const CustomizeTitle = styled.text`
  color: #fff;

  font-family: Nunito;
  font-size: 48px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  @media (max-width: 800px) {
    font-size: 28px;
    text-align: center;
  }
`;

const DemoLessonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 80px;
  gap: 80px;
  width: 100%;
  max-width: 1400px;
  margin: auto;
  min-height: 500px;
  @media (max-width: 767px) {
    padding: 40px 20px;
  }
  & > *:nth-child(even) {
    flex-direction: row-reverse;
    @media (max-width: 767px) {
      flex-direction: column; /* Stack the divs on smaller screens */
      justify-content: center;
      align-items: center;
      gap: 20px;
    }
  }
`;

const DemoLessonInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
  justify-content: center;
  @media (max-width: 767px) {
    flex-direction: column; /* Stack the divs on smaller screens */
    justify-content: center;
    align-items: center;
  }
`;

const DemoLessonSlidesContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  background: #fff;

  /* Barely floating */
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
`;

const DemoLessonSlidesContainerV2 = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 5px;
  background: #fff;

  /* Barely floating */
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
`;

const DemoLessonDetailsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  align-items: start;
  width: 100%;
`;

const LessonDetailsTitle = styled.div`
  color: #282f33;
  font-family: Nunito;
  font-size: 28px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  @media (max-width: 767px) {
    font-size: 22px;
    align-self: flex-start;
  }
`;

const DemoLessonDetailsInfoContainer = styled.div`
  display: flex;
  padding: 30px;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  align-self: stretch;
  border-radius: 5px;
  border: 8px solid #282f33;
  min-height: 200px;
`;

const LessonDetailsLabel = styled.div`
  color: #565760;
  font-family: Nunito;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const LessonDetailsValue = styled.div`
  color: #000;
  font-family: Nunito;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const LessonInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SlidesControlContainer = styled.div`
  display: flex;
  padding: 10px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SlidesControlContainerV2 = styled.div`
  display: flex;
  padding: 10px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  border-radius: 10px;
  background: rgba(40, 47, 51, 0.85);
  position: absolute;
  bottom: 0;
  right: 0;
`;

const SlidesControlLeftContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;
const SlidesControlRightContainer = styled.div`
  display: flex;
  gap: 30px;
  flex-direction: row;
`;

const SlideControllerText = styled.div`
  color: #282f33;
  font-family: Nunito;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;
const SlideControllerTextV2 = styled.div`
  color: white;
  font-family: Nunito;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const PresentButton = styled.div`
  display: flex;
  height: 42px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  align-self: stretch;
  border-radius: 5px;
  background: #fff;
  cursor: pointer;

  /* Barely floating */
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
`;

const PresentButtonText = styled.div`
  color: #282f33;
  font-family: Nunito;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const CopyEditButton = styled.div`
  display: flex;
  height: 42px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  align-self: stretch;
  cursor: pointer;
  border-radius: 5px;
  border: 2px solid #282f33;
`;

const CopyEditButtonText = styled.div`
  color: #282f33;
  font-family: Nunito;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const GetLessonButton = styled(Button)`
  width: fit-content;
  min-height: 60px;
  min-width: 200px;
  font-size: 24px;
  font-weight: bolder;
  background-color: #f99746 !important;
  color: white !important;
  @media (max-width: 767px) {
  }
`;

const TextWithToggle = ({ text, limit }: { text: string; limit: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const displayedText = isExpanded
    ? text
    : text.substring(0, limit) + (text.length > limit ? '...' : '');

  return (
    <div>
      <p>{displayedText}</p>
      {text.length > limit && (
        <Button
          onClick={toggleExpansion}
          type="ghost"
          styles={{ width: 'fit-content', border: 'none', alignSelf: 'flex-end' }}
          icon={
            <IconWrapper
              height="25px"
              width="25px"
              style={{
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(-90deg)',
              }}
            >
              <ArrowLeftSvg />
            </IconWrapper>
          }
        >
          {isExpanded ? 'see less' : 'See more'}
        </Button>
      )}
    </div>
  );
};

const StyledIconWrapper = styled(IconWrapper)`
  padding: 6px;

  border-radius: 50%;
  &:hover {
    background: #ebebeb;
  }
`;

const StyledIconWrapperV2 = styled(IconWrapper)`
  padding: 6px;

  border-radius: 50%;
`;
